import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DateTime, Info } from "luxon";

import BoxEditForm from "../../components/box-edit-form/box-edit-form.component";
import { Button, Card, Chip, Modal, PageSpinner } from "../../components/ui";
import { AlertIcon, PencilIcon, PlusIcon } from "../../components/ui/icons";
import { LOW_STOCK_THRESHOLD, alarmLabel } from "../../utilities/adherence";
import { rolePriority } from "../../utilities/constants";

const FREQUENCY = ["Once daily", "Twice daily", "Three times daily", "Four times daily", "Five times daily"];

const scheduleSummary = (alarms = []) => {
  const active = alarms.filter((code) => code > 0).sort((a, b) => a - b);
  if (!active.length) return "No active alarms";
  const frequency = FREQUENCY[active.length - 1] || `${active.length}× daily`;
  return `${frequency} — ${active.map(alarmLabel).join(" · ")}`;
};

const Schedule = () => {
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(0);
  const [isTimezoneDiff, setIsTimezoneDiff] = useState(false);
  const [editModalBoxNumber, setEditModalBoxNumber] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { deviceId } = useParams();

  const loadDeviceInfo = useCallback(async () => {
    try {
      const { data } = await axios.get(`/app-api/device/${deviceId}/schedule`);
      if (data.deviceInfo) setDeviceInfo(data.deviceInfo);
      if (data.role) setCurrentUserRole(data.role);
      if (data.deviceTimezone) {
        const zone = Info.normalizeZone(data.deviceTimezone.timezoneValue);
        if (zone.offset(0) !== DateTime.now().offset) setIsTimezoneDiff(true);
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not load the schedule");
    }
  }, [deviceId]);

  useEffect(() => {
    loadDeviceInfo();
  }, [loadDeviceInfo]);

  const handleBoxDataSave = async (boxIndex, medicineName, quantity, dose, alarms) => {
    setIsSaving(true);
    try {
      await axios.post(`/app-api/device/${deviceId}/box`, {
        alarms,
        dose,
        quantity,
        medicineName,
        box: boxIndex,
      });
      const updated = [...deviceInfo];
      updated[boxIndex - 1] = { medicineName, quantity, dose, alarms };
      setDeviceInfo(updated);
      setIsEditModalOpen(false);
      toast.success(medicineName ? `Box ${boxIndex} updated!` : `Box ${boxIndex} cleared`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data || "Could not save the compartment");
    } finally {
      setIsSaving(false);
    }
  };

  if (!deviceInfo) return <PageSpinner />;

  const canEdit = currentUserRole >= rolePriority.Member;
  const editingBox = deviceInfo[editModalBoxNumber - 1];

  return (
    <>
      <p className="mb-5 text-sm text-gray-500">Each prescription maps to one compartment on the device.</p>

      {isTimezoneDiff && (
        <Card className="mb-5 flex items-start gap-3 border-amber-200 bg-amber-50 p-4">
          <AlertIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-semibold text-primary-900">
              You seem to be in a different timezone than this device
            </p>
            <p className="text-xs text-gray-600">Alarm times below are in this MedpeR's local timezone.</p>
          </div>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {deviceInfo.map((box, index) => {
          const boxNumber = index + 1;
          const openEditor = () => {
            setEditModalBoxNumber(boxNumber);
            setIsEditModalOpen(true);
          };

          if (!box.medicineName) {
            return (
              <Card
                key={boxNumber}
                className="flex flex-col items-center justify-center border-dashed py-8 text-center"
                style={{ borderStyle: "dashed" }}
              >
                <p className="mb-1 text-xs font-semibold text-gray-500">Box {boxNumber}</p>
                <p className="mb-3 text-xs text-gray-500">Empty compartment</p>
                {canEdit && (
                  <Button variant="secondary" size="sm" onClick={openEditor}>
                    <PlusIcon className="h-3.5 w-3.5" />
                    Add Pill
                  </Button>
                )}
              </Card>
            );
          }

          const low = box.quantity <= LOW_STOCK_THRESHOLD;
          return (
            <Card key={boxNumber} className="p-5" style={low ? { borderColor: "#fcd9b1" } : undefined}>
              <div className="mb-3 flex items-center justify-between gap-2">
                <Chip tone="brand">Box {boxNumber}</Chip>
                {low ? (
                  <Chip tone="warning">Low: {box.quantity} left</Chip>
                ) : (
                  <span className="text-[11px] text-gray-500">Qty {box.quantity}</span>
                )}
              </div>
              <p className="text-sm font-bold text-primary-900">{box.medicineName}</p>
              <p className="mb-3 mt-1 text-xs text-gray-500">{scheduleSummary(box.alarms)}</p>
              {box.dose > 0 && <p className="mb-3 text-[11px] text-gray-500">Dosage {box.dose}</p>}
              {canEdit && (
                <button
                  onClick={openEditor}
                  className="flex items-center gap-1 text-xs font-semibold text-primary-600"
                >
                  <PencilIcon className="h-3.5 w-3.5" />
                  Edit
                </button>
              )}
            </Card>
          );
        })}
      </div>

      <Modal
        isOpen={isEditModalOpen && Boolean(editModalBoxNumber)}
        onRequestClose={() => setIsEditModalOpen(false)}
        onAfterClose={() => setEditModalBoxNumber(0)}
        title={`${editingBox?.medicineName ? "Edit Schedule" : "Add Pill"} — Box ${editModalBoxNumber}`}
      >
        {editingBox && (
          <BoxEditForm
            key={editModalBoxNumber}
            boxNumber={editModalBoxNumber}
            boxInfo={editingBox}
            isSaving={isSaving}
            setIsEditModalOpen={setIsEditModalOpen}
            handleBoxDataSave={handleBoxDataSave}
          />
        )}
      </Modal>
    </>
  );
};

export default Schedule;
